import React, { useState, useEffect } from 'react';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../hooks/AuthProvider';
import AddStoreWorkflow from '../AddStore/AddStoreWorkflow';
import StoreDetails from '../StoreDetails/StoreDetails';
import StripeConnectionModal from '../StoreManagement/StripeConnectionModal';
import MenuOverrideModal from '../StoreManagement/MenuOverrideModal';
import MenuChat from '../MenuChat/MenuChat';
import './ControlPanel.css';

type NavTab = 'stores' | 'theme' | 'icon' | 'menu-only' | 'app-control';

interface App {
  id: string;
  name: string;
  type?: string;
  region?: string;
  organizationId: string;
  appIcon?: string;
  status?: string;
}

interface Store {
  storeId?: string;
  name: string;
  description?: string;
  address?: string;
  city: string;
  state?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  timezone?: string;
  currency?: string;
  appId: string;
  orgId: string;
  stripeAccount: string;
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  stripeTestOnboardingCompletedAt?: Date | any;
  stripeTestConnectedAt?: Date | any;
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  stripeLiveOnboardingCompletedAt?: Date | any;
  stripeLiveConnectedAt?: Date | any;
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
  menuData?: any;
}

interface DeploymentStatus {
  projectId?: string;
  status?: string;
  details?: string;
  timestamp?: string;
  url?: string;
  currentStep?: string;
  requestId?: string;
  error?: string;
  updateAvailable?: boolean;
  theme?: {
    variant: string;
    accentColor: string | null;
    customTheme: any | null;
    hasCustomTheme: boolean;
  };
  appIconUrl?: string | null;
  logoUrl?: string | null;
  menuOnly?: boolean;
  deploymentPayload?: {
    appIconUrl?: string | null;
    logoUrl?: string | null;
    [key: string]: any;
  };
}

interface Theme {
  backgroundColor: string;
  textColor: string;
  cardColor: string;
  topbarBackgroundColor: string;
  topbarIconColor: string;
  topbarBorderBottomColor: string;
  sidebarBackgroundColor: string;
  sidebarIconColor: string;
  sidebarIconColorSelected: string;
  sidebarTextColor: string;
  sidebarBorderColor: string;
  borderColor: string;
  categoryIndicatorColor: string;
  cartBackgroundColor: string;
  cartCardColor: string;
  cartTextColor: string;
  cartButtonColor: string;
  cartButtonTextColor: string;
  cartSummaryBorder: string;
  payButtonColor: string;
  payButtonTextColor: string;
  loaderColor: string;
  [key: string]: string;
}

interface ControlPanelProps {
  db: Firestore;
  functions: Functions;
  appData: App;
  organizationId: string;
  onBack: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  enableTestStripe?: boolean;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  db,
  functions,
  appData,
  organizationId,
  onBack,
  onError,
  onSuccess,
  setLoading,
  enableTestStripe = true,
}) => {
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState<NavTab>('stores');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Store state
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);

  // Deployment
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [retryingDeployment, setRetryingDeployment] = useState(false);

  // Info/status overlays
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);

  // Store sub-views
  const [showAddStore, setShowAddStore] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showStripeConnection, setShowStripeConnection] = useState(false);
  const [showMenuOverride, setShowMenuOverride] = useState(false);
  const [showMenuChat, setShowMenuChat] = useState(false);
  const [stripeEnvironment, setStripeEnvironment] = useState<'test' | 'live'>('live');

  // Theme (inline pane)
  const [themeUpdateMode, setThemeUpdateMode] = useState<'default' | 'custom'>('default');
  const [selectedTheme, setSelectedTheme] = useState<'modern-light' | 'modern-dark'>('modern-light');
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [customTheme, setCustomTheme] = useState<Theme | null>(null);
  const [customThemeError, setCustomThemeError] = useState('');
  const [customThemeFileName, setCustomThemeFileName] = useState('');
  const [updatingTheme, setUpdatingTheme] = useState(false);

  // Icon modal (accessible from Theme pane)
  const [showIconModal, setShowIconModal] = useState(false);
  const [appIconUrl, setAppIconUrl] = useState('');
  const [iconPreviewUrl, setIconPreviewUrl] = useState('');
  const [updatingIcon, setUpdatingIcon] = useState(false);
  const [uploadingIconFile, setUploadingIconFile] = useState(false);

  // Logo state
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [uploadingLogoFile, setUploadingLogoFile] = useState(false);

  // Menu Only (inline pane)
  const [menuOnlyValue, setMenuOnlyValue] = useState(false);
  const [updatingMenuOnly, setUpdatingMenuOnly] = useState(false);

  const createStoreFunc = httpsCallable(functions, 'createStore');
  const completeStoreSetupFunc = httpsCallable(functions, 'completeStoreSetup');

  // ── Data ───────────────────────────────────────────────────────

  const loadStores = async () => {
    console.log("APP DATA: ", appData)
    if (!appData?.id) { setStoresLoading(false); return; }
    setStoresLoading(true);
    try {
      const q = query(collection(db, 'stores'), where('appId', '==', appData.id));
      const snap = await getDocs(q);
      const list: Store[] = [];
      snap.forEach((doc) => {
        const data = doc.data() as Store;
        const toDate = (v: any): Date =>
          v instanceof Timestamp ? v.toDate()
          : v && typeof v === 'object' && 'toDate' in v ? (v as any).toDate()
          : new Date(v || Date.now());
        list.push({
          ...data,
          storeId: doc.id,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
          stripeTestConnected: data.stripeTestConnected || false,
          stripeLiveConnected: data.stripeLiveConnected || false,
          stripeConnected: data.stripeConnected || false,
          isActive: data.isActive ?? false,
        });
      });
      setStores(list);
    } catch (err) {
      console.error('Error loading stores:', err);
      onError('Failed to load stores.');
    } finally {
      setStoresLoading(false);
    }
  };

  const checkDeploymentStatus = async () => {
    if (!appData?.id) return;
    setDeploymentLoading(true);
    try {
      const check = httpsCallable(functions, 'checkInstanceDeploymentStatus');
      const result = await check({ appId: appData.id });
      const status = result.data as DeploymentStatus;
      setDeploymentStatus(status);
      // Pre-populate icon and logo from deploymentPayload
      const payload = (status as any)?.deploymentPayload;
      if (payload?.appIconUrl) { setAppIconUrl(payload.appIconUrl); setIconPreviewUrl(payload.appIconUrl); }
      if (payload?.logoUrl) { setLogoUrl(payload.logoUrl); setLogoPreviewUrl(payload.logoUrl); }
      if (status?.theme) {
        if (status.theme.hasCustomTheme) {
          setThemeUpdateMode('custom');
          setCustomTheme(status.theme.customTheme);
          setCustomThemeFileName('current-custom-theme.json');
        } else {
          setThemeUpdateMode('default');
          setSelectedTheme((status.theme.variant as 'modern-light' | 'modern-dark') || 'modern-light');
          setAccentColor(status.theme.accentColor || '#2563eb');
        }
      }
      setMenuOnlyValue(status?.menuOnly || false);
    } catch (err) {
      console.error('Error checking deployment status:', err);
      setDeploymentStatus(null);
    } finally {
      setDeploymentLoading(false);
    }
  };

  useEffect(() => { loadStores(); }, [appData?.id]);
  // useEffect(() => { checkDeploymentStatus(); }, [appData?.id]);
  // useEffect(() => { checkDeploymentStatus(); }, [stores.length]);

  // Pre-populate icon URL when switching to icon tab
  useEffect(() => {
    if (activeTab === 'icon') {
      const payload = (deploymentStatus as any)?.deploymentPayload;
      const curIcon = payload?.appIconUrl || deploymentStatus?.appIconUrl || '';
      setAppIconUrl(curIcon);
      setIconPreviewUrl(curIcon);
      const curLogo = payload?.logoUrl || deploymentStatus?.logoUrl || '';
      setLogoUrl(curLogo);
      setLogoPreviewUrl(curLogo);
    }
  }, [activeTab]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const accountId = p.get('account_id'), storeId = p.get('store_id'),
          appId = p.get('app_id'), environment = p.get('environment') || 'live';
    if (accountId && storeId && appId && appId === appData.id) {
      (async () => {
        setLoading(true);
        try {
          const result = await completeStoreSetupFunc({ accountId, storeId, appId, environment });
          const rd = result.data as any;
          if (rd?.success) { onSuccess(rd.message); await loadStores(); window.history.replaceState({}, document.title, window.location.pathname); }
          else onError('Store setup could not be completed');
        } catch (err: any) { onError(err.message || 'Failed to complete store setup'); }
        finally { setLoading(false); }
      })();
    }
  }, [appData.id]);

  // ── Helpers ────────────────────────────────────────────────────

  const getMaxStores = () => {
    switch (appData.type) {
      case 'single-store': return 1;
      case 'single-store-multi-location': return 10;
      case 'multi-store': return 100;
      default: return 1;
    }
  };
  const canAddMoreStores = () => appData.type === 'single-store' ? stores.length === 0 : stores.length < getMaxStores();

  const getStatusColor = (status?: string) => {
    if (!status) return 'unknown';
    if (status === 'completed') return 'success';
    if (status === 'failed' || status === 'error') return 'error';
    if (status === 'in-progress' || status === 'pending') return 'progress';
    return 'unknown';
  };

  const handleStoreSubmit = async (storeData: Omit<Store, 'appId' | 'orgId' | 'stripeAccount'>) => {
    if (!currentUser?.email) { onError('User email not available.'); return; }
    setLoading(true);
    try {
      const result = await createStoreFunc({ storeData, appId: appData.id });
      const rd = result.data as any;
      if (rd?.success) { await loadStores(); setShowAddStore(false); onSuccess(rd.message || `Store "${storeData.name}" created!`); }
      else onError('Failed to create store.');
    } catch (err: any) { onError(err.message || 'Failed to create store.'); }
    finally { setLoading(false); }
  };

  const handleCancelAddStore = () => {
    setShowAddStore(false);
    loadStores();
  };

  const handleConnectStripe = (store: Store, environment: 'test' | 'live') => {
    if (environment === 'test' && !enableTestStripe) { onError('Test Stripe disabled.'); return; }
    setSelectedStore(store); setStripeEnvironment(environment); setShowStripeConnection(true);
  };

  const handleRetryDeployment = async () => {
    if (!appData?.id) return;
    setRetryingDeployment(true);
    try {
      await httpsCallable(functions, 'retryInstanceDeployment')({ appId: appData.id });
      onSuccess('Deployment retry initiated');
      setTimeout(checkDeploymentStatus, 2000);
    } catch (err: any) { onError(err.message || 'Failed to retry'); }
    finally { setRetryingDeployment(false); }
  };

  // Theme
  const parseCustomTheme = (jsonText: string): { theme: Theme | null; error: string } => {
    try {
      const parsed = JSON.parse(jsonText);
      const td = parsed.customTheme || parsed;
      const required = ['backgroundColor','textColor','cardColor','topbarBackgroundColor','topbarIconColor','topbarBorderBottomColor','sidebarBackgroundColor','sidebarIconColor','sidebarIconColorSelected','sidebarTextColor','sidebarBorderColor','borderColor','categoryIndicatorColor','cartBackgroundColor','cartCardColor','cartTextColor','cartButtonColor','cartButtonTextColor','cartSummaryBorder','payButtonColor','payButtonTextColor','loaderColor'];
      for (const f of required) if (!td[f]) return { theme: null, error: `Missing: ${f}` };
      if (Object.keys(td).length > 40) return { theme: null, error: 'Too many fields (max 40).' };
      return { theme: td as Theme, error: '' };
    } catch { return { theme: null, error: 'Invalid JSON.' }; }
  };

  const processThemeFile = (file: File) => {
    setCustomThemeError(''); setCustomThemeFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { theme, error } = parseCustomTheme(e.target?.result as string);
      if (theme) { setCustomTheme(theme); setCustomThemeError(''); }
      else { setCustomTheme(null); setCustomThemeError(error); }
    };
    reader.readAsText(file);
  };

  const handleSubmitThemeUpdate = async () => {
    if (!appData?.id) return;
    if (themeUpdateMode === 'custom' && !customTheme) { onError('Please upload a valid custom theme'); return; }
    setUpdatingTheme(true);
    try {
      const params: any = { appId: appData.id };
      if (themeUpdateMode === 'default') { params.theme = selectedTheme; params.themeAccentColor = accentColor; }
      else if (customTheme) params.customTheme = customTheme;
      await httpsCallable(functions, 'retryInstanceDeployment')(params);
      onSuccess('Theme update initiated!');
      // setTimeout(checkDeploymentStatus, 2000);
    } catch (err: any) { onError(err.message || 'Failed to update theme'); }
    finally { setUpdatingTheme(false); }
  };

  // Icon
  const handleOpenIconModal = () => {
    const payload = (deploymentStatus as any)?.deploymentPayload;
    const cur = payload?.appIconUrl || deploymentStatus?.appIconUrl || '';
    setAppIconUrl(cur); setIconPreviewUrl(cur); setShowIconModal(true);
  };

  const handleIconFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith('image/')) { onError('Please select an image.'); return; }
    if (file.size > 5 * 1024 * 1024) { onError('Max 5MB.'); return; }
    setUploadingIconFile(true);
    try {
      const storageRef = ref(getStorage(), `app-assets/${organizationId}/icons/${Date.now()}-${file.name}`);
      const snap = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snap.ref);
      setAppIconUrl(url); setIconPreviewUrl(url); onSuccess('Icon uploaded!');
    } catch { onError('Failed to upload icon.'); }
    finally { setUploadingIconFile(false); }
  };

  const handleLogoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith('image/')) { onError('Please select an image.'); return; }
    if (file.size > 5 * 1024 * 1024) { onError('Max 5MB.'); return; }
    setUploadingLogoFile(true);
    try {
      const storageRef = ref(getStorage(), `app-assets/${organizationId}/logos/${Date.now()}-${file.name}`);
      const snap = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snap.ref);
      setLogoUrl(url); setLogoPreviewUrl(url); onSuccess('Logo uploaded!');
    } catch { onError('Failed to upload logo.'); }
    finally { setUploadingLogoFile(false); }
  };

  const handleSubmitIconUpdate = async () => {
    if (!appIconUrl.trim() && !logoUrl.trim()) { onError('Please provide at least an icon or logo URL'); return; }
    setUpdatingIcon(true);
    try {
      const params: any = { appId: appData.id };
      if (appIconUrl.trim()) params.appIconUrl = appIconUrl.trim();
      if (logoUrl.trim()) params.logoUrl = logoUrl.trim();
      await httpsCallable(functions, 'retryInstanceDeployment')(params);
      onSuccess('Logo / Icon update initiated!');
      setShowIconModal(false); setAppIconUrl(''); setIconPreviewUrl(''); setLogoUrl(''); setLogoPreviewUrl('');
      // setTimeout(checkDeploymentStatus, 2000);
    } catch (err: any) { onError(err.message || 'Failed to update icon'); }
    finally { setUpdatingIcon(false); }
  };

  // Menu Only
  const handleSubmitMenuOnlyUpdate = async () => {
    if (!appData?.id) return;
    setUpdatingMenuOnly(true);
    try {
      await httpsCallable(functions, 'retryInstanceDeployment')({ appId: appData.id, menuOnly: menuOnlyValue });
      onSuccess(`Menu Only ${menuOnlyValue ? 'enabled' : 'disabled'}!`);
      // setTimeout(checkDeploymentStatus, 2000);
    } catch (err: any) { onError(err.message || 'Failed to update'); }
    finally { setUpdatingMenuOnly(false); }
  };

  // ── Sub-views (full page takeovers) ───────────────────────────

  if (showAddStore) {
    return (
      <AddStoreWorkflow
        functions={functions} onSubmit={handleStoreSubmit} onCancel={handleCancelAddStore}
        appRegion={appData.region || 'us-east1'} organizationId={organizationId}
        appId={appData.id} appName={appData.name} appType={appData.type || 'single-store'}
      />
    );
  }
  // if (selectedStore && !showStripeConnection && !showMenuOverride && !showMenuChat) {
  //   return (
  //     <StoreDetails
  //       store={selectedStore} functions={functions} onBack={() => setSelectedStore(null)}
  //       onError={onError} onSuccess={onSuccess} setLoading={setLoading}
  //       onStoreUpdated={loadStores} onUpdateMenu={() => setShowMenuOverride(true)}
  //     />
  //   );
  // }
  if (showMenuChat && selectedStore) {
    return (
      <MenuChat
        store={selectedStore} functions={functions} appId={appData.id}
        onBack={() => { setShowMenuChat(false); setSelectedStore(null); }}
      />
    );
  }

  // ── Nav config ─────────────────────────────────────────────────

  const navItems: { id: NavTab; label: string; icon: React.ReactNode; badge?: React.ReactNode }[] = [
    {
      id: 'stores',
      label: 'Stores',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9,22 9,12 15,12 15,22"/>
        </svg>
      ),
      badge: stores.length === 0 && !storesLoading
        ? <span className="cp-nav-badge warn">!</span>
        : <span className="cp-nav-badge count">{stores.length}</span>,
    },
    {
      id: 'theme',
      label: 'Theme',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/>
          <circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/>
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
        </svg>
      ),
    },
    {
      id: 'icon',
      label: 'Logo / Icon',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
      ),
    },
    {
      id: 'menu-only',
      label: 'Menu Only Mode',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12h18M3 6h18M3 18h18"/>
        </svg>
      ),
      badge: deploymentStatus?.menuOnly ? <span className="cp-nav-badge on">On</span> : undefined,
    },
    {
      id: 'app-control',
      label: 'App Control Panel',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="cp-root">

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="cp-topbar">
        {/* Mobile: hamburger to toggle sidebar */}
        <button className="cp-hamburger" onClick={() => setMobileNavOpen(v => !v)} aria-label="Menu">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        <div className="cp-topbar-left">
          <button className="cp-back-btn" onClick={onBack}>
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <span className="cp-back-label">Sites</span>
          </button>
          <div className="cp-topbar-divider-v" />
          <div className="cp-title-group">
            <span className="cp-title">{appData.name}</span>
            {deploymentStatus?.status && (
              <span className={`cp-status-pill ${getStatusColor(deploymentStatus.status)}`}>
                {deploymentStatus.status}
              </span>
            )}
          </div>
        </div>

        <div className="cp-topbar-right">
          <button
            className={`cp-topbar-btn ${showInfoPanel ? 'active' : ''}`}
            onClick={() => { setShowInfoPanel(v => !v); setShowStatusPanel(false); }}
            title="App Information"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
            </svg>
            <span className="cp-btn-label">Info</span>
          </button>

          <button
            className={`cp-topbar-btn ${showStatusPanel ? 'active' : ''} ${deploymentStatus?.error ? 'has-error' : ''}`}
            onClick={() => { setShowStatusPanel(v => !v); setShowInfoPanel(false); }}
            title="Deployment Status"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span className="cp-btn-label">Deployment</span>
            {deploymentStatus?.error && <span className="cp-error-dot" />}
          </button>
        </div>
      </header>

      {/* ── Overlay panels ──────────────────────────────────────── */}
      {(showInfoPanel || showStatusPanel) && (
        <div className="cp-overlay-backdrop" onClick={() => { setShowInfoPanel(false); setShowStatusPanel(false); }} />
      )}

      {showInfoPanel && (
        <div className="cp-overlay-panel">
          <div className="cp-overlay-header">
            <h3>App Information</h3>
            <button className="cp-overlay-close" onClick={() => setShowInfoPanel(false)}>×</button>
          </div>
          <div className="cp-overlay-body">
            {([
              ['App ID', appData.id],
              ['Name', appData.name],
              ['Type', appData.type || 'Unknown'],
              ['Region', appData.region || 'Unknown'],
              ['Organization', organizationId],
            ] as [string, string][]).map(([label, val]) => (
              <div key={label} className="cp-info-row">
                <span className="cp-info-label">{label}</span>
                <span className="cp-info-value">{val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showStatusPanel && (
        <div className="cp-overlay-panel">
          <div className="cp-overlay-header">
            <h3>Deployment Status</h3>
            <button className="cp-overlay-close" onClick={() => setShowStatusPanel(false)}>×</button>
          </div>
          <div className="cp-overlay-body">
            {deploymentLoading ? (
              <div className="cp-overlay-loading"><div className="cp-spinner" /><span>Checking…</span></div>
            ) : deploymentStatus ? (
              <>
                {deploymentStatus.error ? (
                  <div className="cp-deploy-error">
                    <span>❌</span>
                    <div>
                      <p className="cp-error-msg">{deploymentStatus.error}</p>
                      {deploymentStatus.requestId && <p className="cp-req-id">Request ID: {deploymentStatus.requestId}</p>}
                    </div>
                  </div>
                ) : (
                  <>
                    {([
                      ['Status', <span className={`cp-deploy-badge ${getStatusColor(deploymentStatus.status)}`}>{deploymentStatus.status || 'Unknown'}</span>],
                      deploymentStatus.currentStep ? ['Current Step', deploymentStatus.currentStep] : null,
                      deploymentStatus.url ? ['URL', <a href={deploymentStatus.url} target="_blank" rel="noopener noreferrer" className="cp-link">{deploymentStatus.url}</a>] : null,
                      deploymentStatus.timestamp ? ['Last Updated', new Date(deploymentStatus.timestamp).toLocaleString()] : null,
                    ] as any[]).filter(Boolean).map((row: any, i: number) => (
                      <div key={i} className="cp-info-row">
                        <span className="cp-info-label">{row[0]}</span>
                        <span className="cp-info-value">{row[1]}</span>
                      </div>
                    ))}
                  </>
                )}
                <div className="cp-deploy-actions">
                  {/* <button className="cp-deploy-action-btn" onClick={checkDeploymentStatus} disabled={deploymentLoading}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                    Refresh
                  </button> */}
                  {deploymentStatus.error && (
                    <button className="cp-deploy-action-btn danger" onClick={handleRetryDeployment} disabled={retryingDeployment}>
                      {retryingDeployment ? 'Retrying…' : 'Retry'}
                    </button>
                  )}
                  {!deploymentStatus.updateAvailable && (
                    <button className="cp-deploy-action-btn primary" onClick={handleRetryDeployment} disabled={retryingDeployment}>
                      {retryingDeployment ? 'Updating…' : 'Update Software'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="cp-muted">No deployment status available.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Layout: sidebar + main ───────────────────────────────── */}
      <div className="cp-layout">

        {/* Mobile nav backdrop */}
        {mobileNavOpen && (
          <div className="cp-mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} />
        )}

        {/* Sidebar */}
        <nav className={`cp-sidebar ${mobileNavOpen ? 'open' : ''}`}>
          <div className="cp-sidebar-app">
            <div className="cp-sidebar-app-icon">
              {appData.appIcon
                ? <img src={appData.appIcon} alt="" />
                : <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  </svg>
              }
            </div>
            <div className="cp-sidebar-app-info">
              <span className="cp-sidebar-app-name">{appData.name}</span>
              <span className="cp-sidebar-app-type">{appData.type || 'app'}</span>
            </div>
          </div>

          <div className="cp-sidebar-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`cp-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => {
                    if (item.id === 'app-control') {
                      window.open(`https://${appData.id}.dineplatform.com/store-login`, '_blank');
                      return;
                    }
                    setActiveTab(item.id);
                    setMobileNavOpen(false);
                  }}
              >
                <span className="cp-nav-icon">{item.icon}</span>
                <span className="cp-nav-label">{item.label}</span>
                {item.badge}
              </button>
            ))}
          </div>
        </nav>

        {/* Main content */}
        <main className="cp-main">

          {/* ── STORES ────────────────────────────────────────── */}
          {activeTab === 'stores' && (
            <div className="cp-pane">
              <div className="cp-pane-header">
                <div>
                  <h2 className="cp-pane-title">Stores</h2>
                  <p className="cp-pane-sub">{stores.length} of {getMaxStores()} · {appData.type}</p>
                </div>
                {canAddMoreStores() ? (
                  <button className="cp-btn-primary" onClick={() => setShowAddStore(true)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Store
                  </button>
                ) : (
                  <></>
                  // <button className="cp-btn-ghost" onClick={() => onSuccess('Request more stores coming soon!')}>Request More</button>
                )}
              </div>

              {storesLoading ? (
                <div className="cp-loading-state"><div className="cp-spinner large" /><span>Loading stores…</span></div>
              ) : stores.length === 0 ? (
                <div className="cp-empty-state">
                  <div className="cp-empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9,22 9,12 15,12 15,22"/>
                    </svg>
                  </div>
                  <h3>No stores yet</h3>
                  <p>Add your first store to start accepting orders.</p>
                  <div className="cp-warn-banner">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    App is incomplete until you add a store with live Stripe.
                  </div>
                  <button className="cp-btn-primary" onClick={() => setShowAddStore(true)}>Add Your First Store</button>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="cp-table-wrap">
                    <table className="cp-table">
                      <thead>
                        <tr>
                          <th>Store</th>
                          <th>Location</th>
                          <th>Status</th>
                          <th>Stripe Test</th>
                          <th>Stripe Live</th>
                          <th>Menu</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {stores.map((store) => (
                          <tr key={store.storeId} className="cp-table-row" onClick={() => setSelectedStore(store)}>
                            <td>
                              <div className="cp-cell-stack">
                                <span className="cp-store-name">{store.name}</span>
                                <span className="cp-store-id">{store.storeId}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cp-cell-stack">
                                <span>{store.city}{store.state ? `, ${store.state}` : ''}</span>
                                <span className="cp-dim">{store.country}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`cp-pill ${store.isActive ? 'success' : 'off'}`}>
                                {store.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {enableTestStripe ? (
                                !store.stripeTestConnected
                                  ? <button className="cp-stripe-btn test" onClick={() => handleConnectStripe(store, 'test')}>Connect</button>
                                  : <div className="cp-stripe-cell">
                                      <span className="cp-pill amber">✓ Test</span>
                                      <button className="cp-link-btn" onClick={() => handleConnectStripe(store, 'test')}>Manage</button>
                                    </div>
                              ) : (
                                <span className={`cp-pill ${store.stripeTestConnected ? 'amber' : 'off'}`}>
                                  {store.stripeTestConnected ? '✓ Connected' : '—'}
                                </span>
                              )}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {!store.stripeLiveConnected
                                ? <button className="cp-stripe-btn live" onClick={() => handleConnectStripe(store, 'live')}>Connect</button>
                                : <div className="cp-stripe-cell">
                                    <span className="cp-pill success">✓ Live</span>
                                    <button className="cp-link-btn" onClick={() => handleConnectStripe(store, 'live')}>Manage</button>
                                  </div>
                              }
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <button className="cp-menu-btn" onClick={() => { setSelectedStore(store); setShowMenuChat(true); }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {store.menuData ? 'AI Chat' : 'Build'}
                                <span className="cp-beta">β</span>
                              </button>
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {/* <button className="cp-ghost-btn" onClick={() => setSelectedStore(store)}>Details →</button> */}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="cp-cards">
                    {stores.map((store) => (
                      <div key={store.storeId} className="cp-store-card" onClick={() => setSelectedStore(store)}>
                        <div className="cp-store-card-head">
                          <div>
                            <div className="cp-store-name">{store.name}</div>
                            <div className="cp-dim" style={{ fontSize: 12, marginTop: 2 }}>{store.city}{store.state ? `, ${store.state}` : ''} · {store.country}</div>
                            <div className="cp-store-id" style={{ marginTop: 3 }}>{store.storeId}</div>
                          </div>
                          <div className="cp-store-card-badges">
                            <span className={`cp-pill ${store.isActive ? 'success' : 'off'}`}>{store.isActive ? 'Active' : 'Inactive'}</span>
                            {store.stripeLiveConnected && <span className="cp-pill success">Live ✓</span>}
                            {store.stripeTestConnected && <span className="cp-pill amber">Test ✓</span>}
                          </div>
                        </div>

                        <div className="cp-store-card-stripe">
                          <div className="cp-store-card-stripe-row">
                            <span className="cp-dim">Stripe Test</span>
                            {enableTestStripe
                              ? !store.stripeTestConnected
                                ? <button className="cp-stripe-btn test" onClick={e => { e.stopPropagation(); handleConnectStripe(store, 'test'); }}>Connect</button>
                                : <button className="cp-link-btn" onClick={e => { e.stopPropagation(); handleConnectStripe(store, 'test'); }}>Manage</button>
                              : <span className={`cp-pill ${store.stripeTestConnected ? 'amber' : 'off'}`}>{store.stripeTestConnected ? '✓' : '—'}</span>
                            }
                          </div>
                          <div className="cp-store-card-stripe-row">
                            <span className="cp-dim">Stripe Live</span>
                            {!store.stripeLiveConnected
                              ? <button className="cp-stripe-btn live" onClick={e => { e.stopPropagation(); handleConnectStripe(store, 'live'); }}>Connect</button>
                              : <button className="cp-link-btn" onClick={e => { e.stopPropagation(); handleConnectStripe(store, 'live'); }}>Manage</button>
                            }
                          </div>
                        </div>

                        <div className="cp-store-card-actions">
                          <button className="cp-menu-btn" onClick={e => { e.stopPropagation(); setSelectedStore(store); setShowMenuChat(true); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                            {store.menuData ? 'AI Chat' : 'Build Menu'} <span className="cp-beta">β</span>
                          </button>
                          {/* <button className="cp-ghost-btn" onClick={e => { e.stopPropagation(); setSelectedStore(store); }}>Details →</button> */}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── THEME ─────────────────────────────────────────── */}
          {activeTab === 'theme' && (
            <div className="cp-pane">
              <div className="cp-pane-header">
                <div>
                  <h2 className="cp-pane-title">Theme</h2>
                  <p className="cp-pane-sub">Customize the look of your app</p>
                </div>
              </div>

              <div className="cp-pane-body">
                <div className="cp-theme-tabs">
                  <button className={`cp-theme-tab ${themeUpdateMode === 'default' ? 'active' : ''}`} onClick={() => setThemeUpdateMode('default')}>Default Theme</button>
                  <button className={`cp-theme-tab ${themeUpdateMode === 'custom' ? 'active' : ''}`} onClick={() => setThemeUpdateMode('custom')}>Custom Theme</button>
                </div>

                {themeUpdateMode === 'default' ? (
                  <div className="cp-theme-section">
                    <div className="cp-field">
                      <label>Variant</label>
                      <div className="cp-theme-toggle">
                        <button className={`cp-theme-toggle-btn ${selectedTheme === 'modern-light' ? 'active' : ''}`} onClick={() => setSelectedTheme('modern-light')}>☀️ Light</button>
                        <button className={`cp-theme-toggle-btn ${selectedTheme === 'modern-dark' ? 'active' : ''}`} onClick={() => setSelectedTheme('modern-dark')}>🌙 Dark</button>
                      </div>
                    </div>
                    <div className="cp-field">
                      <label>Accent Color</label>
                      <div className="cp-color-row">
                        <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="cp-color-input" />
                        <input type="text" value={accentColor} onChange={e => setAccentColor(e.target.value)} placeholder="#2563eb" className="cp-text-input mono" style={{ maxWidth: 120 }} />
                      </div>
                      <div className="cp-color-presets">
                        {['#2563eb','#7c3aed','#dc2626','#059669','#ea580c','#0891b2'].map(c => (
                          <button key={c} className="cp-color-preset" style={{ backgroundColor: c }} onClick={() => setAccentColor(c)} />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="cp-theme-section">
                    <div className="cp-info-box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                        <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                      </svg>
                      <p>
                        {deploymentStatus?.url
                          ? <>Build a custom theme at <a href={`${deploymentStatus.url}/theme-customizer`} target="_blank" rel="noopener noreferrer" className="cp-link">{deploymentStatus.url}/theme-customizer</a></>
                          : 'Visit your app\'s /theme-customizer to build a custom theme JSON.'}
                      </p>
                    </div>

                    {!customTheme ? (
                      <div>
                        <label className="cp-upload-label">
                          <input type="file" accept=".json" onChange={e => { const f = e.target.files?.[0]; if (f) processThemeFile(f); }} className="cp-hidden-input" />
                          <div
                            className="cp-upload-area"
                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={e => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.json')) processThemeFile(f); else setCustomThemeError('JSON files only.'); }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <p>Click or drag to upload theme JSON</p>
                          </div>
                        </label>
                        {customThemeError && <div className="cp-field-error">❌ {customThemeError}</div>}
                      </div>
                    ) : (
                      <div>
                        <div className="cp-theme-file-row">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/>
                          </svg>
                          <span className="cp-file-name">{customThemeFileName}</span>
                          <span style={{ fontSize: 12, color: '#34d399' }}>✅ Loaded</span>
                          <button className="cp-remove-btn" onClick={() => { setCustomTheme(null); setCustomThemeFileName(''); }}>Remove</button>
                        </div>
                        <div className="cp-color-swatches">
                          {[['Background', customTheme.backgroundColor], ['Text', customTheme.textColor], ['Card', customTheme.cardColor], ['Topbar', customTheme.topbarBackgroundColor]].map(([l, c]) => (
                            <div key={l} className="cp-swatch-item">
                              <div className="cp-swatch" style={{ backgroundColor: c }} />
                              <span className="cp-dim" style={{ fontSize: 11 }}>{l}</span>
                              <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#9ca3af' }}>{c}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="cp-pane-footer">
                  {/* <p className="cp-update-note"><strong>Note:</strong> Applying the theme triggers a redeployment. This may take a few minutes.</p> */}
                  <button className="cp-btn-primary" onClick={handleSubmitThemeUpdate} disabled={updatingTheme || (themeUpdateMode === 'custom' && !customTheme)}>
                    {updatingTheme ? <><div className="cp-spinner small" /> Applying…</> : 'Apply Theme'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── LOGO / ICON ───────────────────────────────────── */}
          {activeTab === 'icon' && (
            <div className="cp-pane">
              <div className="cp-pane-header">
                <div>
                  <h2 className="cp-pane-title">Logo / Icon</h2>
                  <p className="cp-pane-sub">Update your app's logo and icon</p>
                </div>
              </div>

              <div className="cp-pane-body">
                <div className="cp-field">
                  <label>App Icon</label>
                  <small className="cp-dim" style={{ marginTop: -4 }}>The square icon used on home screens and app listings (512×512px min)</small>
                  <div className="cp-icon-upload-row">
                    <button className="cp-btn-ghost" onClick={() => document.getElementById('cp-icon-file-pane')?.click()} disabled={uploadingIconFile}>
                      {uploadingIconFile ? <><div className="cp-spinner small" /> Uploading…</> : 'Choose File'}
                    </button>
                    <input id="cp-icon-file-pane" type="file" accept="image/*" onChange={handleIconFileUpload} style={{ display: 'none' }} />
                    <input type="url" value={appIconUrl} onChange={e => { setAppIconUrl(e.target.value); setIconPreviewUrl(e.target.value); }} placeholder="Or paste image URL" className="cp-text-input" disabled={uploadingIconFile} />
                  </div>
                  {iconPreviewUrl && (
                    <div className="cp-icon-preview">
                      <img src={iconPreviewUrl} alt="Icon preview" onError={e => { e.currentTarget.style.display = 'none'; }} />
                      <button className="cp-remove-btn" onClick={() => { setAppIconUrl(''); setIconPreviewUrl(''); }}>Remove</button>
                    </div>
                  )}
                </div>

                <div className="cp-field">
                  <label>Logo</label>
                  <small className="cp-dim" style={{ marginTop: -4 }}>The full logo displayed in the app header (wide format recommended)</small>
                  <div className="cp-icon-upload-row">
                    <button className="cp-btn-ghost" onClick={() => document.getElementById('cp-logo-file-pane')?.click()} disabled={uploadingLogoFile}>
                      {uploadingLogoFile ? <><div className="cp-spinner small" /> Uploading…</> : 'Choose File'}
                    </button>
                    <input id="cp-logo-file-pane" type="file" accept="image/*" onChange={handleLogoFileUpload} style={{ display: 'none' }} />
                    <input type="url" value={logoUrl} onChange={e => { setLogoUrl(e.target.value); setLogoPreviewUrl(e.target.value); }} placeholder="Or paste image URL" className="cp-text-input" disabled={uploadingLogoFile} />
                  </div>
                  {logoPreviewUrl && (
                    <div className="cp-icon-preview">
                      <img src={logoPreviewUrl} alt="Logo preview" onError={e => { e.currentTarget.style.display = 'none'; }} />
                      <button className="cp-remove-btn" onClick={() => { setLogoUrl(''); setLogoPreviewUrl(''); }}>Remove</button>
                    </div>
                  )}
                </div>

                <div className="cp-pane-footer">
                  <button className="cp-btn-primary" onClick={handleSubmitIconUpdate} disabled={updatingIcon || (!appIconUrl.trim() && !logoUrl.trim())}>
                    {updatingIcon ? <><div className="cp-spinner small" /> Updating…</> : 'Apply Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── MENU ONLY ─────────────────────────────────────── */}
          {activeTab === 'menu-only' && (
            <div className="cp-pane">
              <div className="cp-pane-header">
                <div>
                  <h2 className="cp-pane-title">Menu Only Mode</h2>
                  <p className="cp-pane-sub">Control ordering availability</p>
                </div>
              </div>

              <div className="cp-pane-body">
                <div className="cp-info-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                  </svg>
                  <p>When enabled, your app displays the menu but disables ordering functionality. Useful for previewing your menu or temporarily pausing orders.</p>
                </div>

                <div className="cp-toggle-card">
                  <div className="cp-toggle-card-text">
                    <span className="cp-toggle-card-label">Menu Only Mode</span>
                    <span className={`cp-toggle-card-status ${menuOnlyValue ? 'on' : 'off'}`}>
                      Currently {menuOnlyValue ? 'enabled' : 'disabled'}
                    </span>
                  </div>
                  <label className="cp-toggle">
                    <input type="checkbox" checked={menuOnlyValue} onChange={e => setMenuOnlyValue(e.target.checked)} />
                    <span className="cp-toggle-track" />
                  </label>
                </div>

                <div className="cp-pane-footer">
                  {/* <p className="cp-update-note"><strong>Note:</strong> Changing this setting triggers a redeployment.</p> */}
                  <button className="cp-btn-primary" onClick={handleSubmitMenuOnlyUpdate} disabled={updatingMenuOnly}>
                    {updatingMenuOnly ? <><div className="cp-spinner small" /> Applying…</> : 'Apply Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── Stripe Modal ─────────────────────────────────────────── */}
      {showStripeConnection && selectedStore && (
        <StripeConnectionModal
          store={selectedStore} appId={appData.id} organizationId={organizationId}
          environment={stripeEnvironment} db={db} functions={functions}
          onSuccess={msg => { onSuccess(msg); loadStores(); }}
          onError={onError}
          onCancel={() => { setShowStripeConnection(false); setSelectedStore(null); }}
          setLoading={setLoading}
        />
      )}

      {/* ── Menu Override Modal ──────────────────────────────────── */}
      {showMenuOverride && selectedStore && (
        <MenuOverrideModal
          store={selectedStore} appId={appData.id} functions={functions}
          onSuccess={msg => { onSuccess(msg); loadStores(); }}
          onError={onError}
          onCancel={() => { setShowMenuOverride(false); setSelectedStore(null); }}
          setLoading={setLoading}
        />
      )}

      {/* ── Icon Modal ───────────────────────────────────────────── */}
      {showIconModal && (
        <div className="cp-modal-overlay">
          <div className="cp-modal narrow">
            <div className="cp-modal-header">
              <h3>Update App Icon</h3>
              <button className="cp-modal-close" onClick={() => { setShowIconModal(false); setAppIconUrl(''); setIconPreviewUrl(''); }}>×</button>
            </div>
            <div className="cp-modal-body">
              <div className="cp-field">
                <label>App Icon</label>
                <div className="cp-icon-upload-row">
                  <button className="cp-btn-ghost" onClick={() => document.getElementById('cp-icon-file')?.click()} disabled={uploadingIconFile}>
                    {uploadingIconFile ? <><div className="cp-spinner small" /> Uploading…</> : 'Choose File'}
                  </button>
                  <input id="cp-icon-file" type="file" accept="image/*" onChange={handleIconFileUpload} style={{ display: 'none' }} />
                  <input type="url" value={appIconUrl} onChange={e => { setAppIconUrl(e.target.value); setIconPreviewUrl(e.target.value); }} placeholder="Or paste URL" className="cp-text-input" disabled={uploadingIconFile} />
                </div>
                {iconPreviewUrl && (
                  <div className="cp-icon-preview">
                    <img src={iconPreviewUrl} alt="Preview" onError={e => { e.currentTarget.style.display = 'none'; }} />
                    <button className="cp-remove-btn" onClick={() => { setAppIconUrl(''); setIconPreviewUrl(''); }}>Remove</button>
                  </div>
                )}
                <small className="cp-dim">Square format recommended (512×512px min)</small>
              </div>
              {/* <p className="cp-update-note"><strong>Note:</strong> Updating triggers a redeployment.</p> */}
            </div>
            <div className="cp-modal-footer">
              <button className="cp-btn-ghost" onClick={() => { setShowIconModal(false); setAppIconUrl(''); setIconPreviewUrl(''); }}>Cancel</button>
              <button className="cp-btn-primary" onClick={handleSubmitIconUpdate} disabled={updatingIcon || !appIconUrl.trim()}>
                {updatingIcon ? <><div className="cp-spinner small" /> Updating…</> : 'Apply Icon'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ControlPanel;